import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { apiClient } from "../../../globals/utils/api";
import Navbar from "../../../globals/components/Navbar";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box,
  TextField,
  Button,
  CircularProgress,
  Typography,
} from "@mui/material";
import Select from "react-select";
import { toast } from "react-toastify";
import { NumericFormat } from "react-number-format";
import DownloadIcon from "@mui/icons-material/Download";

const TrusteeRider = () => {
  const { bond_id } = useParams();
  const navigate = useNavigate();

  const [bondData, setBondData] = useState(null);
  const [bondNumberOptions, setBondNumberOptions] = useState([]);
  const [form, setForm] = useState({
    legalName: "",
    trustName: "",
    bond_number: null,
    county: "",
    courtCaseNumber: "",
    bond_amount: "",
  });

  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bondNumberAssigned, setBondNumberAssigned] = useState(false);
  const [counties, setCounties] = useState([]);
  const [state, setState] = useState(6);
  const [loadingCounties, setLoadingCounties] = useState(false);
  const [endorsementLogs, setEndorsementLogs] = useState([]);
  const [endorsementDocument, setEndorsementDocument] = useState(null);

  const [endorsementFields, setEndorsementFields] = useState({
    date: "",
    type: "",
    from: "",
    to: "",
    endorsement_from_county: "",
    endorsement_to_county: "",
    effective: "",
    signed_date: "",
  });

  // Type options for the select dropdown (values match EndorsementLogs / announcements-recent)
  const typeOptions = [
    { label: "Increase", value: "Increase" },
    { label: "Decrease", value: "Decrease" },
    { label: "Name Change (Principal)", value: "Name_Change_Principal" },
    { label: "Name Change (County)", value: "Name_Change_County" },
    { label: "Name Change (Deceased/Ward/Beneficiary)", value: "Name_Change_Deceased_Ward_Beneficiary" },
  ];
  const AMOUNT_TYPES = ["Increase", "Decrease"];
  const NAME_PRINCIPAL_TYPES = ["Name_Change_Principal", "Name_Change_County", "Name_Change_Deceased_Ward_Beneficiary"];

  function extractDayMonthYear(dateString) {
    if (!dateString) return { day: "", month: "", year: "" };

    // Handle date string safely to avoid timezone issues
    const dateOnly = dateString.split("T")[0]; // Extract YYYY-MM-DD part
    const [year, month, day] = dateOnly.split("-");

    const months = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December"
    ];

    return {
      day: String(parseInt(day, 10)).padStart(2, "0"),
      month: months[parseInt(month, 10) - 1], // month is 1-based in date string
      year: String(parseInt(year, 10)),
    };
  }

  function formatToUSDate(dateStr) {
    if (!dateStr) return "";

    // Handle date string safely to avoid timezone issues
    const dateOnly = dateStr.split("T")[0]; // Extract YYYY-MM-DD part
    const [year, month, day] = dateOnly.split("-");

    // Convert to MM/DD/YYYY format
    const mm = String(parseInt(month, 10)).padStart(2, "0");
    const dd = String(parseInt(day, 10)).padStart(2, "0");
    const yyyy = String(parseInt(year, 10));

    return `${mm}/${dd}/${yyyy}`;
  }

  // Fetch bond details & endorsement logs
  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const data = await apiClient(`/retrieve-trustee-bond/${bond_id}/`);
        setBondData(data);
        if (data.bond_number) setBondNumberAssigned(true);
      } catch (error) {
        toast.error("Failed to fetch bond data.");
      } finally {
        setIsLoading(false);
      }
    }
    async function fetchEndorsementLogs() {
      setIsLoading(true);
      try {
        const response = await apiClient(`/endorsement-logs/${bond_id}/`);
        setEndorsementLogs(response || []);
      } catch (error) {
        toast.error("Failed to load endorsement history");
      } finally {
        setIsLoading(false);
      }
    }
    fetchEndorsementLogs();
    fetchData();
  }, [bond_id]);

  useEffect(() => {
    if (!bond_id) return;
    const fetchEndorsementDocument = async () => {
      try {
        const res = await apiClient(`/endorsement-document/?bond_id=${bond_id}`, "GET");
        setEndorsementDocument(res?.endorsement_document_link != null ? res : null);
      } catch {
        setEndorsementDocument(null);
      }
    };
    fetchEndorsementDocument();
  }, [bond_id]);

  useEffect(() => {
    async function fetchBondNumbers() {
      try {
        const response = await apiClient("/bondnumbers/", "GET");
        setBondNumberOptions(
          response.map((bond) => ({
            label: bond.bond_number,
            value: bond.bond_number,
            ...bond,
          }))
        );
      } catch (err) {
        toast.error("Failed to fetch bond numbers.");
      }
    }
    fetchBondNumbers();
  }, []);

  useEffect(() => {
    if (!bondData || bondNumberOptions.length === 0) return;
    const selectedBondNumber = bondNumberOptions.find(
      (option) =>
        option.value === bondData.bond_number?.bond_number ||
        option.value === bondData.bond_number?.value ||
        option.value === bondData.bond_number ||
        option.label === bondData.bond_number
    );
    setState(bondData.state || 6);
    setForm({
      legalName: bondData.trustee_bond?.legal_name || "",
      trustName: bondData.trustee_bond?.trust_name || "",
      bond_number: selectedBondNumber || null,
      county: bondData.county || "",
      courtCaseNumber: bondData.court_case_number || "",
      bond_amount: bondData.bond_amount || "",
    });
  }, [bondData, bondNumberOptions]);

  useEffect(() => {
    if (!bondData || endorsementLogs.length === 0) return;
    let relevantDate = "";
    if (endorsementLogs.length === 1) {
      relevantDate = bondData.created_at;
    } else if (endorsementLogs.length > 1) {
      relevantDate = endorsementLogs[1].endorsed_at;
    }
    const now = new Date();
    const signedDateISO = now.toISOString().split("T")[0];

    const currentType = endorsementLogs[0]?.endorsement_type || "";
    const isAmountType = ["Increase", "Decrease"].includes(currentType);
    const isCountyType = currentType === "Name_Change_County";
    const fromValue = isAmountType
      ? (endorsementLogs[0]?.endorsement_from_amount != null
          ? Number(endorsementLogs[0].endorsement_from_amount).toLocaleString(
              "en-US",
              { style: "currency", currency: "USD", minimumFractionDigits: 2 }
            )
          : "")
      : isCountyType
        ? (endorsementLogs[0]?.endorsement_from_county ?? "")
        : (endorsementLogs[0]?.endorsement_from_principal ?? "");
    const toValue = isAmountType
      ? (endorsementLogs[0]?.endorsement_to_amount != null
          ? Number(endorsementLogs[0].endorsement_to_amount).toLocaleString(
              "en-US",
              { style: "currency", currency: "USD", minimumFractionDigits: 2 }
            )
          : "")
      : isCountyType
        ? (endorsementLogs[0]?.endorsement_to_county ?? "")
        : (endorsementLogs[0]?.endorsement_to_principal ?? "");

    setEndorsementFields({
      date: relevantDate ? relevantDate.split("T")[0] : "",
      type: currentType,
      from: fromValue,
      to: toValue,
      endorsement_from_county: isCountyType ? (endorsementLogs[0]?.endorsement_from_county ?? "") : "",
      endorsement_to_county: isCountyType ? (endorsementLogs[0]?.endorsement_to_county ?? "") : "",
      effective: "",
      signed_date: signedDateISO,
    });
  }, [bondData, endorsementLogs]);

  // Counties for dropdown
  useEffect(() => {
    const fetchCounties = async () => {
      if (state) {
        setLoadingCounties(true);
        try {
          const countyResponse = await apiClient(
            `/counties-by-state/${state}/`,
            "GET"
          );
          const formattedCounties = countyResponse.map((county) => ({
            label: county.bond_county,
            value: county.bond_county,
          }));
          setCounties(formattedCounties);
        } catch (error) {
          // silent
        } finally {
          setLoadingCounties(false);
        }
      }
    };
    fetchCounties();
  }, [state]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };
  const handleEndorsementFieldChange = (e) => {
    setEndorsementFields({
      ...endorsementFields,
      [e.target.name]: e.target.value,
    });
  };
  const handleBondNumberChange = (selected) => {
    setForm({ ...form, bond_number: selected });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    if (!endorsementFields.effective) {
      toast.error("Please fill the Effective Date for Rider.");
      setIsSubmitting(false);
      return;
    }

    const parseCurrency = (str) => {
      if (!str) return null;
      const cleaned = String(str).replace(/[^0-9.-]+/g, "");
      const value = cleaned ? Number(cleaned) : null;
      return Number.isNaN(value) ? null : value;
    };

    const newBondAmount = NAME_PRINCIPAL_TYPES.includes(endorsementFields.type)
      ? bondData?.bond_amount
      : parseCurrency(endorsementFields.to);

    // PATCH trustee bond
    const patchPayload = {
      bond_amount: newBondAmount,
      county: form.county,
      court_case_number: form.courtCaseNumber,
      trustee_bond: {
        legal_name: form.legalName,
        trust_name: form.trustName,
      },
    };
    try {
      await apiClient(
        `/update_trustee_bond/${bond_id}/`,
        "PATCH",
        patchPayload
      );
      const data = await apiClient(`/retrieve-trustee-bond/${bond_id}/`);
      const { day, month, year } = extractDayMonthYear(endorsementFields.date);
      const {
        day: signed_day,
        month: signed_month,
        year: signed_year,
      } = extractDayMonthYear(endorsementFields.signed_date);
      const approvePayload = {
        ...data,
        bondNumber: data.bond_number?.bondno_id,
        is_rider: true,
        rider_data: {
          day,
          month,
          year,
          type: endorsementFields.type,
          from: endorsementFields.endorsement_from_county || endorsementFields.from,
          to: endorsementFields.endorsement_to_county || endorsementFields.to,
          effective: formatToUSDate(endorsementFields.effective),
          signed_day,
          signed_month,
          signed_year,
        },
      };
      await apiClient(
        `/approve_trustee_bond/${bond_id}/`,
        "POST",
        approvePayload
      );
      toast.success("Bond rider released successfully.");
    } catch (err) {
      toast.error("Failed to release bond rider.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading && !bondData) {
    return (
      <Box display="flex" justifyContent="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Navbar />
      <Box
        sx={{
          maxWidth: 1280,
          mx: "auto",
          mt: 2,
          mb: 2,
          p: 4,
          background: "#fafbfc",
          borderRadius: 2,
          boxShadow: "0 2px 8px #0001",
        }}
        component="form"
        onSubmit={handleSubmit}
      >
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h5" fontWeight="bold">
            Trustee Rider
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
            {endorsementDocument?.endorsement_document_link && (
              <Button
                variant="outlined"
                color="primary"
                sx={{ px: 1, py: 1 }}
                startIcon={<DownloadIcon />}
                onClick={() =>
                  window.open(
                    endorsementDocument.endorsement_document_link,
                    "_blank",
                    "noopener,noreferrer"
                  )
                }
              >
                View Endorsement Document
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate(`/vermost-trustee-bond-update/${bond_id}`)}
              sx={{ px: 1, py: 1 }}
            >
              View all bond details
            </Button>
          </Box>
        </Box>
        {/* --- Rider Endorsement Fields --- */}
        <Box mb={2}>
          <Typography fontWeight="bold" mb={2}>
            Endorsement Change Details
          </Typography>
          <Box display="flex" gap={2} mb={2} mt={2}>
            <TextField
              label="Last Date of Change"
              type="date"
              name="date"
              value={endorsementFields.date}
              onChange={handleEndorsementFieldChange}
              InputLabelProps={{ shrink: true }}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Type"
              select
              fullWidth
              value={endorsementFields.type}
              onChange={(e) => setEndorsementFields((prev) => ({ ...prev, type: e.target.value }))}
              SelectProps={{ native: true }}
              sx={{ flex: 1 }}
            >
              <option value=""></option>
              {typeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </TextField>
          </Box>
          <Box display="flex" gap={2} mb={2}>
            {AMOUNT_TYPES.includes(endorsementFields.type) ? (
              <>
                <NumericFormat
                  customInput={TextField}
                  label="From Amount"
                  name="from"
                  value={endorsementFields.from}
                  onValueChange={(values) =>
                    setEndorsementFields((prev) => ({
                      ...prev,
                      from: values.value
                        ? Number(values.value).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                          })
                        : "",
                    }))
                  }
                  thousandSeparator
                  isNumericString
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  sx={{ flex: 1 }}
                />
                <NumericFormat
                  customInput={TextField}
                  label="To Amount"
                  name="to"
                  value={endorsementFields.to}
                  onValueChange={(values) =>
                    setEndorsementFields((prev) => ({
                      ...prev,
                      to: values.value
                        ? Number(values.value).toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                          })
                        : "",
                    }))
                  }
                  thousandSeparator
                  isNumericString
                  prefix="$"
                  decimalScale={2}
                  fixedDecimalScale
                  allowNegative={false}
                  sx={{ flex: 1 }}
                />
              </>
            ) : NAME_PRINCIPAL_TYPES.includes(endorsementFields.type) ? (
              endorsementFields.type === "Name_Change_County" ? (
                <>
                  <Autocomplete
                    options={counties}
                    loading={loadingCounties}
                    value={
                      endorsementFields.endorsement_from_county
                        ? counties.find((c) => c.value === endorsementFields.endorsement_from_county) || null
                        : null
                    }
                    onChange={(e, selected) =>
                      setEndorsementFields((prev) => ({
                        ...prev,
                        endorsement_from_county: selected ? selected.value : "",
                        from: selected ? selected.value : "",
                      }))
                    }
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                      option && value && option.value === value.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="From County"
                        margin="dense"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCounties ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ flex: 1 }}
                  />
                  <Autocomplete
                    options={counties}
                    loading={loadingCounties}
                    value={
                      endorsementFields.endorsement_to_county
                        ? counties.find((c) => c.value === endorsementFields.endorsement_to_county) || null
                        : null
                    }
                    onChange={(e, selected) =>
                      setEndorsementFields((prev) => ({
                        ...prev,
                        endorsement_to_county: selected ? selected.value : "",
                        to: selected ? selected.value : "",
                      }))
                    }
                    getOptionLabel={(option) =>
                      typeof option === "string" ? option : option.label
                    }
                    isOptionEqualToValue={(option, value) =>
                      option && value && option.value === value.value
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="To County"
                        margin="dense"
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {loadingCounties ? (
                                <CircularProgress color="inherit" size={18} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                    sx={{ flex: 1 }}
                  />
                </>
              ) : endorsementFields.type === "Name_Change_Deceased_Ward_Beneficiary" ? (
                <>
                  <TextField
                    label="From Legal Name"
                    name="from"
                    value={endorsementFields.from}
                    onChange={(e) =>
                      setEndorsementFields((prev) => ({ ...prev, from: e.target.value }))
                    }
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="To Legal Name"
                    name="to"
                    value={endorsementFields.to}
                    onChange={(e) =>
                      setEndorsementFields((prev) => ({ ...prev, to: e.target.value }))
                    }
                    sx={{ flex: 1 }}
                  />
                </>
              ) : (
                <>
                  <TextField
                    label="From Principal"
                    name="from"
                    value={endorsementFields.from}
                    onChange={(e) =>
                      setEndorsementFields((prev) => ({ ...prev, from: e.target.value }))
                    }
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="To Principal"
                    name="to"
                    value={endorsementFields.to}
                    onChange={(e) =>
                      setEndorsementFields((prev) => ({ ...prev, to: e.target.value }))
                    }
                    sx={{ flex: 1 }}
                  />
                </>
              )
            ) : null}
          </Box>
          <TextField
            label="Effective Date"
            type="date"
            name="effective"
            value={endorsementFields.effective}
            onChange={handleEndorsementFieldChange}
            InputLabelProps={{ shrink: true, sx: { fontWeight: "bold" } }}
            sx={{ mb: 2 }}
            fullWidth
          />
          <TextField
            label="Signed Date"
            type="date"
            name="signed_date"
            value={endorsementFields.signed_date}
            onChange={handleEndorsementFieldChange}
            InputLabelProps={{ shrink: true }}
            sx={{ mb: 2 }}
            fullWidth
          />
        </Box>
        <Typography fontWeight="bold" mb={1}>
          Bond Review
        </Typography>
        <Box mb={2}>
          <TextField
            label="Legal Name"
            name="legalName"
            fullWidth
            value={form.legalName}
            onChange={handleChange}
            margin="dense"
          />
        </Box>
        <Box mb={2}>
          <TextField
            label="Trust Name"
            name="trustName"
            fullWidth
            value={form.trustName}
            onChange={handleChange}
            margin="dense"
          />
        </Box>
        <Box mb={2}>
          <Autocomplete
            options={counties}
            loading={loadingCounties}
            value={
              form.county
                ? counties.find((c) => c.value === form.county) || null
                : null
            }
            onChange={(e, selected) =>
              setForm({ ...form, county: selected ? selected.value : "" })
            }
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="County"
                variant="outlined"
                margin="dense"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingCounties ? (
                        <CircularProgress color="inherit" size={18} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        </Box>
        <Box mb={2}>
          <TextField
            label="Court Case Number"
            name="courtCaseNumber"
            fullWidth
            value={form.courtCaseNumber}
            onChange={handleChange}
            margin="dense"
          />
        </Box>
        <Box mb={2}>
          <Select
            options={bondNumberOptions}
            value={form.bond_number}
            onChange={handleBondNumberChange}
            isDisabled={bondNumberAssigned}
            isClearable
            isSearchable
            placeholder="Select Bond Number"
            getOptionLabel={(option) => {
              if (option.is_used) return `${option.label} (Assigned)`;
              return `${option.label}`;
            }}
            getOptionValue={(option) => option.value}
            menuPortalTarget={document.body}
            styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
          />
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={3}>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            disabled={isSubmitting}
            endIcon={isSubmitting && <CircularProgress size={18} />}
          >
            {isSubmitting ? "Releasing..." : "Release Bond Rider"}
          </Button>
        </Box>
      </Box>
    </>
  );
};

export default TrusteeRider;
